/* eslint-disable @typescript-eslint/no-explicit-any */
import StyledImage from '@apps/components/Image';
import { getDateSlug } from '@apps/utils/get-date-slug';
import ChevronLeftIcon from '@stack/assets/icons/chevron-left.svg';
import ChevronRightIcon from '@stack/assets/icons/chevron-right.svg';
import avatarPlaceholder from '@stack/assets/images/avatar_placeholder.jpeg';
import { toLower } from 'lodash';
import Link from 'next/link';
import useTranslation from 'next-translate/useTranslation';
import styled from 'styled-components';

const ImageWrapper = styled.div`
  width: 50px;
  height: 50px;
  min-width: 50px;
  min-height: 50px;
  display: block;
  border-radius: 50%;
  overflow: hidden;

  @media only screen and (max-width: 768px) {
    display: none;
  }
`;

export type NextPrevNewsProps = {
  nextNews: {
    title: string;
    slug: string;
    avatar: string;
    id: number;
    createdAt: string;
  };
  prevNews: {
    title: string;
    slug: string;
    avatar: string;
    id: number;
    createdAt: string;
  };
  webView?: boolean;
};

/**
 * ===========================
 * MAIN
 * ===========================
 */
export const NextPrevNews: React.FC<NextPrevNewsProps> = (props) => {
  const { nextNews, prevNews, webView } = props;

  // ======================= HOOKS
  const { t } = useTranslation('common');

  // ======================= EVENTS
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getPostHref = (post: any) => {
    if (!post?.title) return '/';
    const dateSlug = getDateSlug(post.createdAt);
    return webView
      ? (`/webview/${dateSlug}/${toLower(post?.slug)}` as string)
      : (`/${dateSlug}/${toLower(post?.slug)}` as string);
  };

  // ======================= RENDER FUNCTIONS
  const renderChevronIconWithLabel = (type: 'next' | 'prev') => {
    const icon = type === 'next' ? <ChevronRightIcon /> : <ChevronLeftIcon />;
    const label =
      type === 'next' ? t('newsDetail.next') : t('newsDetail.previous');
    return (
      <div
        style={{
          display: 'flex',
          gap: 6,
          flexDirection: type === 'next' ? 'row-reverse' : 'row',
          alignItems: 'center',
        }}
      >
        <div
          style={{
            display: 'flex',
            width: 11,
            height: 11,
          }}
        >
          {icon}
        </div>
        <span
          style={{
            fontSize: 11,
            fontWeight: 400,
            textTransform: 'uppercase',
          }}
        >
          {label}
        </span>
      </div>
    );
  };

  const renderNextPrevButton = (
    type: 'next' | 'prev',
    // href: string,
    post: {
      title: string;
      slug: string;
      avatar: string;
      id: number;
      createdAt: string;
    }
  ) => {
    const href: string = getPostHref(post) as string;
    return (
      <Link href={href} className="url-ani-2">
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
          }}
        >
          {renderChevronIconWithLabel(type)}
          <div
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'center',
              flexDirection: type === 'next' ? 'row-reverse' : 'row',
            }}
          >
            <ImageWrapper>
              <StyledImage
                src={post.avatar || avatarPlaceholder.src}
                alt=""
                width={50}
                height={50}
              />
            </ImageWrapper>
            <p
              style={{
                fontSize: 16,
                fontWeight: 500,
                lineHeight: '24px',
                letterSpacing: '-0.25px',
                textAlign: type === 'next' ? 'right' : 'left',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                maxWidth: '300px',
              }}
            >
              {post?.title}
            </p>
          </div>
        </div>
      </Link>
    );
  };

  // ======================= VIEWS
  return (
    <div
      style={{
        display: 'flex',
        width: '100%',
        justifyContent: 'space-between',
        gap: 100,
      }}
    >
      {renderNextPrevButton('prev', prevNews)}
      {renderNextPrevButton('next', nextNews)}
    </div>
  );
};

/**
 * ===========================
 * EXPORTS
 * ===========================
 */
export default NextPrevNews;
